import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getFacilities, getOrganizationProfile, updateFacility, updateOrganizationProfile, type FacilityInput } from '../../lib/services/facilities'

type OrganizationDraft = {
  name?: string
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  employee_count?: string
  notes?: string
}

type FacilityDraft = Partial<FacilityInput>

function emptyFacilityForm(): FacilityInput {
  return {
    facility_name: '',
    state: '',
    facility_type: 'ASC',
    address: '',
    city: '',
    zip_code: '',
    license_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    employee_count: null,
    specialties: '',
    notes: '',
  }
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const welcome = searchParams.get('welcome') === '1'

  const organizationQuery = useQuery({
    queryKey: ['organization-profile'],
    queryFn: getOrganizationProfile,
  })
  const facilitiesQuery = useQuery({
    queryKey: ['onboarding-facilities'],
    queryFn: getFacilities,
  })

  const [organizationDraft, setOrganizationDraft] = useState<OrganizationDraft>({})
  const [facilityDraft, setFacilityDraft] = useState<FacilityDraft>({})
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const organizationForm = {
    name: organizationDraft.name ?? organizationQuery.data?.name ?? '',
    contact_name: organizationDraft.contact_name ?? organizationQuery.data?.contact_name ?? '',
    contact_email: organizationDraft.contact_email ?? organizationQuery.data?.contact_email ?? '',
    contact_phone: organizationDraft.contact_phone ?? organizationQuery.data?.contact_phone ?? '',
    address: organizationDraft.address ?? organizationQuery.data?.address ?? '',
    city: organizationDraft.city ?? organizationQuery.data?.city ?? '',
    state: organizationDraft.state ?? organizationQuery.data?.state ?? '',
    zip_code: organizationDraft.zip_code ?? organizationQuery.data?.zip_code ?? '',
    employee_count:
      organizationDraft.employee_count ??
      (organizationQuery.data?.employee_count === null || organizationQuery.data?.employee_count === undefined
        ? ''
        : String(organizationQuery.data.employee_count)),
    notes: organizationDraft.notes ?? organizationQuery.data?.notes ?? '',
  }

  const firstFacility = facilitiesQuery.data?.[0]
  const facilityForm: FacilityInput = {
    ...emptyFacilityForm(),
    ...(firstFacility
      ? {
          facility_name: firstFacility.facility_name,
          state: firstFacility.state ?? '',
          facility_type: firstFacility.facility_type ?? 'ASC',
          address: firstFacility.address ?? '',
          city: firstFacility.city ?? '',
          zip_code: firstFacility.zip_code ?? '',
          license_number: firstFacility.license_number ?? '',
          contact_name: firstFacility.contact_name ?? '',
          contact_email: firstFacility.contact_email ?? '',
          contact_phone: firstFacility.contact_phone ?? '',
          employee_count: firstFacility.employee_count,
          specialties: firstFacility.specialties.join(', '),
          notes: firstFacility.notes ?? '',
        }
      : {}),
    ...facilityDraft,
  }

  const organizationMutation = useMutation({
    mutationFn: async () =>
      await updateOrganizationProfile({
        name: organizationForm.name,
        contact_name: organizationForm.contact_name,
        contact_email: organizationForm.contact_email,
        contact_phone: organizationForm.contact_phone,
        address: organizationForm.address,
        city: organizationForm.city,
        state: organizationForm.state,
        zip_code: organizationForm.zip_code,
        employee_count: organizationForm.employee_count.trim() === '' ? null : Number.parseInt(organizationForm.employee_count, 10),
        notes: organizationForm.notes,
      }),
  })

  const facilityMutation = useMutation({
    mutationFn: async () => {
      const facilityId = facilitiesQuery.data?.[0]?.facility_id
      if (!facilityId) {
        throw new Error('A bootstrap facility was not created for this tenant.')
      }
      return await updateFacility(facilityId, facilityForm)
    },
  })

  async function handleSave() {
    setError(null)
    setMessage(null)
    try {
      await organizationMutation.mutateAsync()
      await facilityMutation.mutateAsync()
      setMessage('Onboarding details saved. Continue to billing when you are ready.')
    } catch (saveError) {
      setError((saveError as Error).message)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Tenant Onboarding</h1>
        <p className="mt-1 text-sm text-slate-600">
          Complete the first-pass organization and facility details, then continue into billing to finish commercial setup.
        </p>
        {welcome && <p className="mt-3 text-sm text-emerald-700">Your trial account is active. Finish bootstrap details before moving into billing.</p>}
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Organization Profile</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.name} onChange={(event) => setOrganizationDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Organization name" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.contact_name} onChange={(event) => setOrganizationDraft((current) => ({ ...current, contact_name: event.target.value }))} placeholder="Primary contact" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.contact_email} onChange={(event) => setOrganizationDraft((current) => ({ ...current, contact_email: event.target.value }))} placeholder="Contact email" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.contact_phone} onChange={(event) => setOrganizationDraft((current) => ({ ...current, contact_phone: event.target.value }))} placeholder="Contact phone" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2" value={organizationForm.address} onChange={(event) => setOrganizationDraft((current) => ({ ...current, address: event.target.value }))} placeholder="Organization address" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.city} onChange={(event) => setOrganizationDraft((current) => ({ ...current, city: event.target.value }))} placeholder="City" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.state} onChange={(event) => setOrganizationDraft((current) => ({ ...current, state: event.target.value.toUpperCase() }))} placeholder="State" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.zip_code} onChange={(event) => setOrganizationDraft((current) => ({ ...current, zip_code: event.target.value }))} placeholder="ZIP code" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={organizationForm.employee_count} onChange={(event) => setOrganizationDraft((current) => ({ ...current, employee_count: event.target.value }))} placeholder="Employee count" />
          <textarea className="rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2" rows={3} value={organizationForm.notes} onChange={(event) => setOrganizationDraft((current) => ({ ...current, notes: event.target.value }))} placeholder="Notes" />
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">First Facility</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.facility_name ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, facility_name: event.target.value }))} placeholder="Facility name" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.facility_type ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, facility_type: event.target.value }))} placeholder="Facility type" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2" value={facilityForm.address ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, address: event.target.value }))} placeholder="Facility address" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.city ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, city: event.target.value }))} placeholder="City" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.state ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, state: event.target.value.toUpperCase() }))} placeholder="State" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.zip_code ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, zip_code: event.target.value }))} placeholder="ZIP code" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.contact_name ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, contact_name: event.target.value }))} placeholder="Facility contact" />
          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={facilityForm.contact_email ?? ''} onChange={(event) => setFacilityDraft((current) => ({ ...current, contact_email: event.target.value }))} placeholder="Facility contact email" />
        </div>
      </section>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-emerald-700">{message}</p>}

      <div className="flex flex-wrap gap-3">
        <button className="btn-primary text-sm" disabled={organizationMutation.isPending || facilityMutation.isPending} onClick={() => void handleSave()}>
          {organizationMutation.isPending || facilityMutation.isPending ? 'Saving...' : 'Save and Continue'}
        </button>
        <button className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700" onClick={() => navigate('/billing')}>
          Go to Billing
        </button>
      </div>
    </div>
  )
}
